import { zodResolver } from '@hookform/resolvers/zod';
import { PlusIcon, XIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/core/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/core/card';
import { Form } from '@/components/core/form';
import { Switch } from '@/components/core/switch';
import { TextFormField } from '@/components/forms/text-form-field';
import { Header, HeaderTitle } from '@/components/header';
import { Page, PageContent } from '@/components/layout';
import { useFeatureFlagConfig } from '@/shared/providers/FeatureFlagConfigProvider';

const schema = z.object({
  key: z.string().trim().min(1),
});

export function Features() {
  const { featureFlags, setFeatureFlag, deleteFeatureFlag } = useFeatureFlagConfig();
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      key: '',
    },
  });

  const onSubmit = form.handleSubmit(({ key }) => {
    setFeatureFlag(key, false);
    setTimeout(form.reset);
  });

  const onToggleFeatureFlag = (featureFlagKey: string, value: boolean) => {
    setFeatureFlag(featureFlagKey, value);
  };

  return (
    <Page>
      <Header>
        <HeaderTitle>Features</HeaderTitle>
      </Header>

      <PageContent variant="column">
        <Card>
          <CardHeader>
            <CardTitle>Frontend Feature Flags</CardTitle>
            <CardDescription>Add and manage feature flags used by the frontend.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            <Form {...form}>
              <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
                <TextFormField
                  control={form.control}
                  name="key"
                  aria-label="Feature flag key"
                  placeholder="Feature flag key"
                  required
                />
                <Button type="submit">
                  <PlusIcon />
                  Add
                </Button>
              </form>
            </Form>

            <div className="flex flex-col gap-2">
              {Object.entries(featureFlags).map(([featureFlagKey, enabled]) => (
                <div
                  key={featureFlagKey}
                  className="flex items-center justify-between rounded-xl border px-3 py-2"
                >
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={enabled}
                      onCheckedChange={(value) => {
                        onToggleFeatureFlag(featureFlagKey, Boolean(value));
                      }}
                      aria-label={featureFlagKey}
                    />
                    <span>{featureFlagKey}</span>
                  </div>

                  <Button
                    aria-label={`Delete ${featureFlagKey}`}
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      deleteFeatureFlag(featureFlagKey);
                    }}
                  >
                    <XIcon />
                    Delete
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </PageContent>
    </Page>
  );
}
